import { SelectData } from "../../models";
import { PillVariant } from "../../widget";
import { OrganisationTypeConstants, OrganisationTypeLabels, UserType } from "../organisation";

export class ProjectAccessConstants {
  public static readonly STATUS_SENT = "Sent";

  public static readonly STATUS_ACCEPTED = "Accepted";

  public static readonly STATUS_REJECTED = "Rejected";

  public static readonly STATUS_EXPIRED = "Expired";

  public static readonly STATUS_REVOKED = "Revoked";

  public static readonly STATUS_PENDING = "Pending";

  // project_organisation_invitation.type - an organisation asking to join, rather than being invited.
  public static readonly TYPE_REQUEST = "Request";

  // How the invitation type reads in the project Access tab's "Invites and requests" table.
  public static readonly getInvitationTypeLabel = (type?: string | null): string =>
    type === ProjectAccessConstants.TYPE_REQUEST ? "Request" : "Invitation";

  public static readonly ACCESS_LEVEL_ROLE_OPTIONS = [
    { key: "Guest - Read", value: "Guest - Read" },
    { key: "Guest - Write", value: "Guest - Write" },
  ];

  public static readonly ACCESS_LEVEL_ROLE_OPTIONS_ASSET_OR_DEVELOPMENT_MANAGER = [
    { key: "Developer", value: "Developer" },
    { key: "Guest - Read", value: "Guest - Read" },
    { key: "Guest - Write", value: "Guest - Write" },
  ];

  // Both mirror the api's resend/revoke validators: an accepted invitation cannot be resent, and a
  // rejected one gets neither action.
  public static readonly canResendInvitation = (status?: string): boolean =>
    status === ProjectAccessConstants.STATUS_SENT ||
    status === ProjectAccessConstants.STATUS_EXPIRED ||
    status === ProjectAccessConstants.STATUS_REVOKED;

  public static readonly canRevokeInvitation = (status?: string): boolean =>
    status === ProjectAccessConstants.STATUS_SENT || status === ProjectAccessConstants.STATUS_ACCEPTED;

  /**
   * What happens to the developer a Developer invite displaces. The keys are the api's
   * AllowedExistingDeveloperActions (Organisation/Resources/Constants.cs) - sending one is what
   * lets the invite past the one-developer-per-project rule (BR-02726 / BR-02873), and an
   * unrecognised value comes back as VALIDATION_INVALID_EXISTING_DEVELOPER_ACTION.
   */
  public static readonly EXISTING_DEVELOPER_ACTION_OPTIONS: SelectData = [
    { key: "REMOVE", value: "Remove from the project" },
    { key: "DOWNGRADE_GUEST_READ", value: "Keep as Guest - Read" },
    { key: "DOWNGRADE_GUEST_WRITE", value: "Keep as Guest - Write" },
  ];

  // Guest roles carry no responsible-owner grants, so the api refuses them as a change-RO target
  // (BR-02871 / BR-02885).
  public static readonly RESPONSIBLE_OWNER_ELIGIBLE_ROLES = ["Developer", "Development Manager", "Asset Manager"];

  // Keyed by the GraphQL enum form, the one spelling that feeds both searchOrganisations and
  // getInvitationAccessOptions without conversion.
  public static readonly INVITATION_ORGANISATION_TYPE_OPTIONS: SelectData = [
    "DEVELOPER",
    "DEVELOPMENT_MANAGER",
    "ASSET_MANAGER",
  ].map((key) => ({ key, value: OrganisationTypeLabels[key] }));

  /**
   * The api takes a concrete role, so each option keys the role sent as targetRole and only the
   * label reads "Full participant" (BR-02870). Guest - Write is Developer-only (BR-02872 / REQ-017).
   * `organisationType` arrives as the GraphQL enum form, so it is matched against the option keys
   * above - full access resolves to the invitee's own type, which only an invitable type has.
   */
  public static readonly getInvitationAccessOptions = (organisationType?: string | null): SelectData => {
    const fullRole = ProjectAccessConstants.INVITATION_ORGANISATION_TYPE_OPTIONS.find(
      (option) => option.key === organisationType
    )?.value;
    const options: SelectData = [];

    if (fullRole) {
      options.push({ key: fullRole, value: ProjectAccessConstants.FULL_PARTICIPANT_LABEL });
    }

    options.push({ key: "Guest - Read", value: "Guest - Read" });

    if (fullRole === OrganisationTypeConstants.DEVELOPER) {
      options.push({ key: "Guest - Write", value: "Guest - Write" });
    }

    return options;
  };

  // searchProjectOrganisations sets no MaxPageSize, so HotChocolate's default of 50 caps the page.
  public static readonly ACCESS_ROWS_PAGE_SIZE = 50;

  public static readonly getAccessLevelRoleOptions = (userType?: UserType): SelectData => {
    switch (userType) {
      case OrganisationTypeConstants.ASSET_MANAGER:
      case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
        return ProjectAccessConstants.ACCESS_LEVEL_ROLE_OPTIONS_ASSET_OR_DEVELOPMENT_MANAGER;
      default:
        return ProjectAccessConstants.ACCESS_LEVEL_ROLE_OPTIONS;
    }
  };

  public static readonly FULL_ACCESS_LABEL = "Full access";

  public static readonly FULL_PARTICIPANT_LABEL = "Full participant";

  public static readonly GUEST_READ_ROLE = "Guest - Read";

  public static readonly GUEST_WRITE_ROLE = "Guest - Write";

  // REQ-013/017, BR-02870/02872 - keyed on the organisation being invited, not on the inviter, so
  // it cannot share getAccessLevelRoleOptions above. "Full access" is the role named after that
  // organisation's own type, which is what the api's AllowedRoles expects as targetRole; Guest -
  // Write is offered for a Developer only. Option keys are sent verbatim, values are display only.
  // KAN-9720 reworks the access picker across every surface - fold this into its version when it
  // lands rather than keeping two.
  public static readonly getProjectBuilderAccessOptions = (organisationType?: string | null): SelectData => {
    const fullAccessRole = organisationType ? OrganisationTypeLabels[organisationType] : undefined;
    if (!fullAccessRole) return [];

    return [
      { key: fullAccessRole, value: ProjectAccessConstants.FULL_ACCESS_LABEL },
      { key: ProjectAccessConstants.GUEST_READ_ROLE, value: ProjectAccessConstants.GUEST_READ_ROLE },
      ...(organisationType === "DEVELOPER"
        ? [{ key: ProjectAccessConstants.GUEST_WRITE_ROLE, value: ProjectAccessConstants.GUEST_WRITE_ROLE }]
        : []),
    ];
  };

  // Every role named after an organisation type is full access, so only the Guest roles keep their own label.
  public static readonly getAccessLevelLabel = (role: string): string =>
    !role || role === ProjectAccessConstants.GUEST_READ_ROLE || role === ProjectAccessConstants.GUEST_WRITE_ROLE
      ? role
      : ProjectAccessConstants.FULL_PARTICIPANT_LABEL;

  public static readonly canRoleBeResponsibleOwner = (role: string): boolean =>
    ProjectAccessConstants.RESPONSIBLE_OWNER_ELIGIBLE_ROLES.includes(role);

  // Full access is the role named after an organisation type; anything else is a Guest role. No
  // permission tells the two apart - an Asset Manager Staff user holds fewer than a Guest - Write one.
  public static readonly isFullParticipantRole = (role?: string): boolean =>
    !!role && ProjectAccessConstants.RESPONSIBLE_OWNER_ELIGIBLE_ROLES.includes(role);

  public static readonly INVITATION_RESPONSE = {
    ACCEPT: "Accept",
    REJECT: "Reject",
  };

  public static readonly PROJECT_ACCESS_STATUS_PILL_VARIANT_MAP: { [key: string]: PillVariant } = {
    Sent: "cyan",
    Accepted: "green",
    Rejected: "red",
    Expired: "coral",
    Revoked: "grey",
    Pending: "yellow",
  };
}
